import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Header from '@components/Header';
import Footer from '@components/Footer';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const styles = theme => ({
  root: {
    paddingTop: 58,
  },
  contentContainer: {
    paddingTop: 55,
    paddingBottom: 55,
  },
});

const Index = ({ classes }) => {
  return (
    <div className={classes.root}>
      <Head>
        <title>Terms of Service - SellOnLive</title>
      </Head>
      <Header />
      <div className={['container', classes.contentContainer].join(' ')}>
        <h1>Sellonlive Tech Term of Service</h1>
        <div className="page-width">
          <p>
            By signing up for a SellOnLive Account (as defined in Section 1) or by using any
            SellOnLive Services (as defined below), you are agreeing to be bound by the following
            terms and conditions (the “Terms of Service”).
          </p>
          <p>
            The services offered by SellOnLive under the Terms of Service include various products
            and services to help you sell goods and services to buyers. Any such services offered by
            SellOnLive are referred to in these Terms of Services as the “Services”. Any new
            features or tools which are added to the current Services shall be also subject to the
            Terms of Service. SellOnLive reserves the right to update and change the Terms of
            Service by posting updates and changes to the SellOnLive website. You are advised to
            check the Terms of Service from time to time for any updates or changes that may impact
            you. and if you do not accept such amendments, you must cease using the Services.
          </p>
          <p>
            You must read, agree with and accept all of the terms and conditions contained or
            expressly referenced in these Terms of Service,
          </p>
          <h2 id="Account">1. Account Terms</h2>
          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    To access and use the Services, you must register for a SellOnLive account
                    (“Account”) by providing your full legal name, current address, phone number, a
                    valid email address, and any other information indicated as required. SellOnLive
                    may reject your application for an Account, or cancel an existing Account, for
                    any reason, in our sole discretion.
                  </li>
                  <li>
                    You must be the older of: (i) 18 years, or (ii) at least the age of majority in
                    the jurisdiction where you reside and from which you use the Services to open an
                    Account.
                  </li>
                  <li>
                    You confirm that you are receiving any Services provided by SellOnLive for the
                    purposes of carrying on a business activity and not for any personal, household
                    or family purpose.
                  </li>
                  <li>
                    You acknowledge that SellOnLive will use the email address you provide on
                    opening an Account or as updated by you from time to time as the primary method
                    for communication with you.
                  </li>
                  <li>
                    You are responsible for keeping your password secure. SellOnLive cannot and will
                    not be liable for any loss or damage from your failure to maintain the security
                    of your Account and password.
                  </li>
                  <li>
                    You are responsible for all activity and content such as photos, images, videos,
                    graphics, written content, audio files, code, information, or data uploaded,
                    collected, generated, stored, displayed, distributed, transmitted or exhibited
                    on or in connection with your Account (“Materials”).
                  </li>
                  <li>
                    A breach or violation of any term in the Terms of Service, including the AUP, as
                    determined in the sole discretion of SellOnLive may result in an immediate
                    termination of your Services.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  You are responsible for your Account and any Materials you upload to the
                  SellOnLive Service. Remember that if you violate these terms we may cancel your
                  service.
                </p>

                <p>If we need to reach you, we will send you an email.</p>
              </div>
            </aside>
          </div>

          <h2 id="Activation">2. Account Activation</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  <strong>2.1 Store Owner</strong>
                </p>

                <ol>
                  <li>
                    Subject to section 2.1(2), the person signing up for the Service by opening an
                    Account will be the contracting party (“Store Owner”) for the purposes of our
                    Terms of Service and will be the person who is authorized to use any
                    corresponding Account we may provide to the Store Owner in connection with the
                    Service. You are responsible for ensuring that the name of the Store Owner
                    (including the legal name of the company that owns the Store, if applicable) is
                    clearly visible on the Store’s website.
                  </li>
                  <li>
                    If you are signing up for the Services on behalf of your employer, your employer
                    shall be the Store Owner. If you are signing up for the Services on behalf of
                    your employer, then you must use your employer-issued email address and you
                    represent and warrant that you have the authority to bind your employer to our
                    Terms of Service.
                  </li>
                  <li>
                    Your SellOnLive Store can only be associated with one Store Owner. A Store Owner
                    may have multiple SellOnLive Stores. “Store” means the online store or physical
                    retail location(s) associated with the Account.
                  </li>
                </ol>

                <p>
                  <strong>2.2 Staff Accounts</strong>
                </p>

                <ol>
                  <li>
                    Based on your SellOnLive pricing plan, you can create one or more staff accounts
                    (“Staff Accounts”) allowing other people to access the Account. With Staff
                    Accounts, the Store Owner can set permissions and let other people work in their
                    Account while determining the level of access by Staff Accounts to specific
                    business information (for example, you can limit Staff Account access to sales
                    information on the Reports page or prevent Staff Accounts from changing general
                    store settings).
                  </li>
                  <li>
                    The Store Owner is responsible and liable for the acts, omissions and defaults
                    arising from use of Staff Accounts in the performance of obligations under these
                    Terms of Service as if they were the Store Owner’s own acts, omissions or
                    defaults.
                  </li>
                  <li>
                    The Store Owner and the users under Staff Accounts are each referred to as a
                    “SellOnLive User”.
                  </li>
                </ol>

                <p>
                  <strong>2.3 SellOnLive Payments Accounts</strong>
                </p>

                <ol>
                  <li>
                    Upon completion of sign up for the Service, SellOnLive will create a PayPal
                    Express Checkout account on your behalf, using your email address. Depending on
                    your location, SellOnLive may also create a SellOnLive Payments account on your
                    behalf.
                  </li>
                  <li>
                    You acknowledge that SellOnLive Payments will be your default payments
                    gateway(s) and that it is your sole responsibility as the Store Owner to
                    activate and maintain these accounts. If you do not wish to keep either of the
                    payment accounts active, it is your responsibility to deactivate them.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  Only one person can be the “Store Owner”, usually the person signing up for the
                  SellOnLive Service. The Store Owner is responsible for the Account, is bound by
                  these Terms of Service and is responsible for the actions of others accessing the
                  Account. If you sign up on behalf of your employer, your employer is the Store
                  Owner responsible for your Account.
                </p>

                <p>
                  We automatically create certain accounts for you to accept payments. You are
                  responsible for activating and deactivating these accounts and complying with
                  their terms, which are with various third parties.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="General">3. General Conditions</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  You must read, agree with and accept all of the terms and conditions contained in
                  these Terms of Service, including the{' '}
                  <a href="/privacy-policy" className="body-link">
                    Privacy Policy
                  </a>
                </p>

                <ol>
                  <li>
                    Technical support in respect of the Services is only provided to SellOnLive
                    Users.
                  </li>
                  <li>
                    The Terms of Service shall be governed by and interpreted in accordance with the
                    laws of the Province of Ontario and the laws of Canada applicable therein,
                    without regard to principles of conflicts of laws. The United Nations Convention
                    on Contracts for the International Sale of Goods will not apply to these Terms
                    of Service and is hereby expressly excluded.
                  </li>
                  <li>
                    You acknowledge and agree that SellOnLive may amend these Terms of Service at
                    any time by posting the relevant amended and restated Terms of Service on
                    SellOnLive’s website, available at{' '}
                    <a href="https://www.sellonlive.tech/terms-of-service" className="body-link">
                      https://www.sellonlive.tech/terms-of-service
                    </a>{' '}
                    and such amendments to the Terms of Service are effective as of the date of
                    posting. Your continued use of the Services after the amended Terms of Service
                    are posted to SellOnLive’s website constitutes your agreement to, and acceptance
                    of, the amended Terms of Service. If you do not agree to any changes to the
                    Terms of Service, do not continue to use the Service. If the EU Terms apply to
                    your Store, then amendments to these Terms of Service will be provided in
                    accordance with the EU Terms.
                  </li>
                  <li>
                    You may not use the SellOnLive Services for any illegal or unauthorized purpose
                    nor may you, in the use of the Service, violate any laws in your jurisdiction
                    (including but not limited to copyright laws), the laws applicable to you in
                    your customer’s jurisdiction, or the laws of Canada and the Province of Ontario.
                    You will comply with all applicable laws, rules and regulations in your use of
                    the Service and your performance of obligations under the Terms of Service.
                  </li>
                  <li>
                    You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion
                    of the Service, use of the Services, or access to the Services without the
                    express written permission by SellOnLive.
                  </li>
                  <li>
                    You shall not purchase search engine or other pay per click keywords (such as
                    Google Ads), or domain names that use SellOnLive or SellOnLive trademarks and/or
                    variations and misspellings thereof.
                  </li>
                  <li>
                    Questions about the Terms of Service should be sent to{' '}
                    <a href="mailto:sellonlivetech@gmail.com" className="body-link">
                      sellonlivetech@gmail.com
                    </a>
                    .
                  </li>
                  <li>
                    You understand that your Materials (not including credit card information), may
                    be transferred unencrypted and involve (a) transmissions over various networks;
                    and (b) changes to conform and adapt to technical requirements of connecting
                    networks or devices. Credit card information is always encrypted during transfer
                    over networks.
                  </li>
                  <li>
                    You acknowledge and agree that your use of the Services, including information
                    transmitted to or stored by SellOnLive, is governed by its privacy policy at{' '}
                    <a href="https://sellonlive.tech/privacy-policy/" className="body-link">
                      https://www.sellonlive.tech/privacy-policy
                    </a>
                  </li>
                  <li>
                    All the terms and provisions of the Terms of Service shall be binding upon and
                    inure to the benefit of the parties to the Terms of Service and to their
                    respective heirs, successors, permitted assigns and legal representatives.
                    SellOnLive shall be permitted to assign these Terms of Service without notice to
                    you or consent from you. You shall have no right to assign or otherwise transfer
                    the Terms of Service, or any of your rights or obligations hereunder, to any
                    third party without SellOnLive’s prior written consent, to be given or withheld
                    in SellOnLive’s sole discretion.
                  </li>
                  <li>
                    If any provision, or portion of the provision, in these Terms of Service is, for
                    any reason, held to be invalid, illegal or unenforceable in any respect, then
                    such invalidity, illegality or unenforceability will not affect any other
                    provision (or the unaffected portion of the provision) of the Terms of Service,
                    and the Terms of Service will be construed as if such invalid, illegal or
                    unenforceable provision, or portion of the provision, had never been contained
                    within the Terms of Service.
                  </li>
                  <li>
                    Sections 1, 3(2)-(5), 6-8, 14-15, 19, 21 and 22 will survive the termination or
                    expiration of these Terms of Service.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  The SellOnLive service belongs to us. You are not allowed to rip it off or use it
                  for any illegal or sketchy purpose
                </p>

                <p>
                  Your Materials may be transferred unencrypted and may be altered, but credit card
                  information is always encrypted.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="SellOnLiveRights">4. SellOnLive Rights</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    We reserve the right to modify or terminate the Services for any reason, without
                    notice at any time. Not all Services and features are available in every
                    jurisdiction and we are under no obligation to make any Services or features
                    available in any jurisdiction.
                  </li>
                  <li>
                    We reserve the right to refuse service to anyone for any reason at any time.
                  </li>
                  <li>
                    Verbal or written abuse of any kind (including threats of abuse or retribution)
                    of any SellOnLive customer, SellOnLive employee, member, or officer will result
                    in immediate Account termination.
                  </li>
                  <li>
                    SellOnLive does not pre-screen Materials and it is in our sole discretion to
                    refuse or remove any Materials from the Service, including your Store.
                  </li>
                  <li>
                    We reserve the right to provide our services to your competitors and make no
                    promise of exclusivity in any particular market segment. You further acknowledge
                    and agree that SellOnLive employees and contractors may also be SellOnLive
                    customers/merchants and that they may compete with you, although they may not
                    use your Confidential Information (as defined in Section 6) in doing so.
                  </li>
                  <li>
                    In the event of a dispute regarding Account ownership, we reserve the right to
                    request documentation to determine or confirm Account ownership. Documentation
                    may include, but is not limited to, a scanned copy of your business license,
                    government issued photo ID, the last four digits of the credit card on file,
                    your status as an employee of an entity, etc.
                  </li>
                  <li>
                    SellOnLive retains the right to determine, in our sole judgment, rightful
                    Account ownership and transfer an Account to the rightful Store Owner. If we are
                    unable to reasonably determine the rightful Store Owner, without prejudice to
                    our other rights and remedies, SellOnLive reserves the right to temporarily
                    disable an Account until resolution has been determined between the disputing
                    parties.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>We can modify, cancel or refuse the service at anytime.</p>

                <p>
                  In the event of an ownership dispute over a SellOnLive account, we can freeze the
                  account or transfer it to the rightful owner, as determined by us.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="ConfidentialInformation">5. Confidentiality</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    <p>
                      “Confidential Information”&nbsp;shall include, but shall not be limited to,
                      any and all information associated with a party’s business and not publicly
                      known, including specific business information, technical processes and
                      formulas, software, customer lists, prospective customer lists, names,
                      addresses and other information regarding customers and prospective customers,
                      product designs, sales, costs (including any relevant processing fees), price
                      lists, and other unpublished financial information, business plans and
                      marketing data, and any other confidential and proprietary information,
                      whether or not marked as confidential or proprietary. SellOnLive’s
                      Confidential Information includes all information that you receive relating to
                      us, or to the Services, that is not known to the general public including
                      information related to our security program and practices.
                    </p>
                  </li>
                  <li>
                    <p>
                      Each party agrees to use the other party’s Confidential Information solely as
                      necessary for performing its obligations under these Terms of Service and in
                      accordance with any other obligations in these Terms of Service including this
                      Section 6. Each party agrees that it shall take all reasonable steps, at least
                      substantially equivalent to the steps it takes to protect its own proprietary
                      information, to prevent the duplication, disclosure or use of any such
                      Confidential Information, other than (i) by or to its employees, agents and
                      subcontractors who must have access to such Confidential Information to
                      perform such party’s obligations hereunder, who each shall treat such
                      Confidential Information as provided herein, and who are each subject to
                      obligations of confidentiality to such party that are at least as stringent as
                      those contained herein; or (ii) as required by any law, regulation, or order
                      of any court of proper jurisdiction over the parties and the subject matter
                      contained in these Terms of Service, provided that, if legally permitted, the
                      receiving party shall give the disclosing party prompt written notice and use
                      commercially reasonable efforts to ensure that such disclosure is accorded
                      confidential treatment. Confidential Information shall not include any
                      information that the receiving party can prove: (A) was already in the public
                      domain, or was already known by or in the possession of the receiving party,
                      at the time of disclosure of such information; (B) is independently developed
                      by the receiving party without use of or reference to the other party’s
                      Confidential Information, and without breaching any provisions of these Terms
                      of Service; or (C) is thereafter rightly obtained by the receiving party from
                      a source other than the disclosing party without breaching any provision of
                      these Terms of Service.
                    </p>
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  Both you and SellOnLive agree to use the Confidential Information of the other
                  only to perform the obligations in these Terms of Service. Confidential
                  Information must be protected and respected.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Liability">6. Limitation of Liability</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    You expressly understand and agree that, to the extent permitted by applicable
                    laws, SellOnLive shall not be liable for any direct, indirect, incidental,
                    special, consequential or exemplary damages, including but not limited to,
                    damages for loss of profits, goodwill, use, data or other intangible losses
                    resulting from the use of or inability to use the Service.
                  </li>
                  <li>
                    To the extent permitted by applicable laws, in no event shall SellOnLive or our
                    suppliers be liable for lost profits or any special, incidental or consequential
                    damages arising out of or in connection with our site, our Services or these
                    Terms of Service (however arising including negligence). You agree to indemnify
                    and hold us and (as applicable) our parent, subsidiaries, affiliates, SellOnLive
                    partners, officers, directors, agents, employees, and suppliers harmless from
                    any claim or demand, including reasonable attorneys’ fees, made by any third
                    party due to or arising out of your breach of these Terms of Service or the
                    documents it incorporates by reference (including the AUP), or your violation of
                    any law or the rights of a third party.
                  </li>
                  <li>
                    Your use of the Services is at your sole risk. The Services are provided on an
                    “as is” and “as available” basis without any warranty or condition, express,
                    implied or statutory.
                  </li>
                  <li>
                    SellOnLive does not warrant that the Services will be uninterrupted, timely,
                    secure, or error-free.
                  </li>
                  <li>
                    SellOnLive does not warrant that the results that may be obtained from the use
                    of the Services will be accurate or reliable.
                  </li>
                  <li>
                    SellOnLive does not warrant that the quality of any products, services,
                    information, or other materials purchased or obtained by you through the
                    Services will meet your expectations, or that any errors in the Services will be
                    corrected.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  We are not responsible if you break the law, breach this agreement or go against
                  the rights of a third party, especially if you get sued.
                </p>

                <p>
                  Service is “as is” so it may have errors or interruptions and we provide no
                  warranties and our liability is limited.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Waiver">7. Waiver, Severability, and Complete Agreement</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  The failure of SellOnLive to exercise or enforce any right or provision of the
                  Terms of Service shall not constitute a waiver of such right or provision. If any
                  provision of the Terms of Service, including all terms and conditions and other
                  documents it incorporates by reference, is held by a court of competent
                  jurisdiction to be contrary to law, such provision shall be changed and
                  interpreted so as to best accomplish the objectives of the original provision to
                  the fullest extent allowed by law, and the remaining provision of the Terms of
                  Service shall remain in full force and effect.
                </p>

                <p>
                  The Terms of Service, including the documents it incorporates by reference,
                  constitute the entire agreement between you and SellOnLive and govern your use of
                  the Services and your Account, superseding any prior agreements between you and
                  SellOnLive (including, but not limited to, any prior versions of the Terms of
                  Service).
                </p>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  If SellOnLive chooses not to enforce any of these provisions at any time, it does
                  not mean that we give up that right later.
                </p>

                <p>
                  These Terms of Service make up the agreement that applies to you. This means that
                  any previous agreements between you and SellOnLive don’t apply if they conflict
                  with these terms.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Ip">8. Intellectual Property and Customer Content</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    We do not claim any intellectual property rights over the Materials you provide
                    to the SellOnLive Service. All Materials you upload remains yours. You can
                    remove your SellOnLive Store at any time by deleting your Account.
                  </li>
                  <li>
                    By uploading Materials, you agree: (a) to allow other internet users to view the
                    Materials you post publicly to your Store; (b) to allow SellOnLive to store, and
                    in the case of Materials you post publicly, display and use your Materials; and
                    (c) that SellOnLive can, at any time, review and delete all the Materials
                    submitted to its Service, although SellOnLive is not obligated to do so.
                  </li>
                  <li>
                    You retain ownership over all Materials that you upload to the Store; however,
                    by making your Store public, you agree to allow others to view Materials that
                    you post publicly to your Store. You are responsible for compliance of the
                    Materials with any applicable laws or regulations.
                  </li>
                  <li>
                    SellOnLive shall have the non-exclusive right and license to use the names,
                    trademarks, service marks and logos associated with your Store to promote the
                    Service.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>Anything you upload remains yours and is your responsibility.</p>
              </div>
            </aside>
          </div>

          <h2 id="Email">9. SellOnLive Email</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  You may generate or send email from your Account using the SellOnLive email
                  services (the “Email Services”). In addition to the terms applicable to the
                  Services generally (including SellOnLive’s
                  <a href="/privacy-policy" className="body-link">
                    Privacy Policy
                  </a>
                  ), the following terms apply to your access and use of the Email Services:
                </p>

                <ol>
                  <li>
                    <p>
                      SellOnLive employs certain controls to scan the content of emails you deliver
                      using the Email Services prior to delivery (“Content Scanning”). Such Content
                      Scanning is intended to limit spam, phishing, or other malicious content that
                      contravenes these Terms of Service, or SellOnLive’s Acceptable Use Policy
                      (collectively, “Threats”). By using the Email Services, you explicitly grant
                      SellOnLive the right to employ such Content Scanning. SellOnLive does not
                      warrant that the Email Services will be free from Threats, and each SellOnLive
                      merchant is responsible for all content generated by their respective Stores.
                    </p>
                  </li>
                  <li>
                    <p>
                      BY GENERATING OR SENDING EMAIL THROUGH THE EMAIL SERVICES, YOU AGREE TO COMPLY
                      WITH THE FOLLOWING REQUIREMENTS (THE “EMAIL SERVICES REQUIREMENTS”).
                      SellOnLive, OR ITS THIRD PARTY PROVIDERS, MAY SUSPEND OR TERMINATE YOUR ACCESS
                      TO AND USE OF THE EMAIL SERVICES IF YOU DO NOT COMPLY WITH THE EMAIL SERVICES
                      REQUIREMENTS.
                    </p>

                    <ol>
                      <li>
                        Your use of the Email Services must comply with all applicable laws.
                        Examples of applicable laws include laws relating to spam or unsolicited
                        commercial email (“UCE”), privacy, security, obscenity, defamation,
                        intellectual property, pornography, terrorism, homeland security, gambling,
                        child protection, and other applicable laws. It is your responsibility to
                        know and understand the laws applicable to your use of the Email Services
                        and the emails you generate or send through the Email Services.
                      </li>
                      <li>
                        Your use of the Email Services must comply with SellOnLive’s
                        <a href="/privacy-policy" className="body-link">
                          Privacy Policy
                        </a>
                        . It is your responsibility to read and understand the Privacy Policy
                        applicable to your use of the Email Services and the emails you generate or
                        send through the Email Services.
                      </li>
                      <li>
                        You will use commercially reasonable efforts not to send sensitive personal
                        data, including information regarding an individual’s medical or health
                        condition, race or ethnic origin, political opinions, religious or
                        philosophical beliefs, or other sensitive data (collectively, “Sensitive
                        Data”) through the Email Services. It is your responsibility to read and
                        understand your obligations in relation to Sensitive Data.
                      </li>
                      <li>
                        Your use of the Email Services must follow all applicable guidelines
                        established by SellOnLive. The guidelines below are examples of practices
                        that may violate the Email Services Requirements when generating, or sending
                        emails through the Email Services:
                        <ul>
                          <li>
                            using non-permission based email lists (i.e., lists in which each
                            recipient has not explicitly granted permission to receive emails from
                            you by affirmatively opting-in to receive those emails);
                          </li>
                          <li>using purchased or rented email lists;</li>
                          <li>
                            using third party email addresses, domain names, or mail servers without
                            proper permission;
                          </li>
                          <li>
                            sending emails to non-specific addresses (e.g.,
                            <a href="mailto:webmaster@domain.com" className="body-link">
                              webmaster@domain.com
                            </a>{' '}
                            or{' '}
                            <a href="mailto:info@domain.com" className="body-link">
                              info@domain.com
                            </a>
                            );&nbsp;
                          </li>
                          <li>
                            sending emails that result in an unacceptable number of spam or UCE
                            complaints (even if the emails themselves are not actually spam or
                            UCE);&nbsp;
                          </li>
                          <li>
                            failing to include a working “unsubscribe” link in each email that
                            allows the recipient to remove themselves from your mailing list;
                          </li>
                          <li>
                            failing to comply with any request from a recipient to be removed from
                            your mailing list within 10 days of receipt of the request;&nbsp;
                          </li>
                          <li>
                            failing to include in each email a link to the then-current privacy
                            policy applicable to that email;&nbsp;
                          </li>
                          <li>
                            disguising the origin or subject matter of any email or falsifying or
                            manipulating the originating email address, subject line, headers, or
                            transmission path information for any email;&nbsp;
                          </li>
                          <li>
                            failing to include in each email your valid physical mailing address or
                            a link to that information; or
                          </li>
                          <li>
                            including “junk mail,” “chain letters,” “pyramid schemes,” incentives
                            (e.g., coupons, discounts, awards, or other incentives) or other
                            material in any email that encourages a recipient to forward the Email
                            to another recipient.
                          </li>
                        </ul>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <p>
                      If You or a customer knows of or suspects any violations of the Email Services
                      Requirements, please notify SellOnLive at{' '}
                      <a href="mailto:sellonlivetech@gmail.com" className="body-link">
                        sellonlivetech@gmail.com
                      </a>
                      . SellOnLive will determine compliance with the Email Services Requirements in
                      its discretion.
                    </p>
                  </li>
                  <li>
                    <p>
                      SellOnLive’s Email Services utilize Third Party Providers, including SendGrid
                      (a Twilio Company). Your use of the Email Services is subject to SendGrid’s{' '}
                      <a href="https://www.twilio.com/legal/aup" className="body-link">
                        Acceptable Use Policy
                      </a>{' '}
                      as it may be amended by SendGrid from time to time.
                    </p>
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  By using the Email Services, you agree that SellOnLive may use certain tools to
                  ensure the content of the emails sent through the service is safe and compliant
                  with our policies.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Fees">10. Payment of Fees</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    You will pay the Fees applicable to your subscription to Online Service and/or
                    any other applicable fees, including but not limited to applicable fees relating
                    to the value of sales made through your Store when using all payment providers
                    other than SellOnLive Payments (“Transaction Fees”), and any fees relating to
                    your purchase or use of any products or services such as SellOnLive Payments,
                    shipping, apps, Themes or Third Party Services (“Additional Fees”). Together,
                    the Subscription Fees, Transaction Fees and the Additional Fees are referred to
                    as the “Fees”.
                  </li>
                  <li>
                    You must keep a valid payment method on file with us to pay for all incurred and
                    recurring Fees. SellOnLive will charge applicable Fees to any valid payment
                    method that you authorize (“Authorized Payment Method”), and SellOnLive will
                    continue to charge the Authorized Payment Method for applicable Fees until the
                    Services are terminated, and any and all outstanding Fees have been paid in
                    full. Unless otherwise indicated, all Fees and other charges are in U.S.
                    dollars, and all payments shall be in U.S. currency.
                  </li>
                  <li>
                    Subscription Fees are paid in advance and will be billed in 30 day intervals
                    (each such date, a “Billing Date”). Transaction Fees and Additional Fees will be
                    charged from time to time at SellOnLive’s discretion. You will be charged on
                    each Billing Date for all outstanding Fees that have not previously been
                    charged. Fees will appear on an invoice, which will be sent to the Store Owner
                    via the email provided. As well, an invoice will appear on the Account page of
                    your Store’s administration console. Users have approximately two weeks to bring
                    up and settle any issues with the billing of Subscription Fees.
                  </li>
                  <li>
                    If we are not able to process payment of Fees using an Authorized Payment
                    Method, we will make a second attempt to process payment using any Authorized
                    Payment Method 3 days later. If the second attempt is not successful, we will
                    make a final attempt 3 days following the second attempt. If our final attempt
                    is not successful, we may suspend and revoke access to your Account and the
                    Services. Your Account will be reactivated upon your payment of any outstanding
                    Fees, plus the Fees applicable to your next billing cycle. You may not be able
                    to access your Account or your storefront during any period of suspension. If
                    the outstanding Fees remain unpaid for 60 days following the date of suspension,
                    SellOnLive reserves the right to terminate your Account.
                  </li>
                  <li>
                    All Fees are exclusive of applicable federal, provincial, state, local or other
                    governmental sales, goods and services (including Goods and Sales Tax under the
                    Goods and Services Tax Act, Chapter 117A of Singapore), harmonized or other
                    taxes, fees or charges now in force or enacted in the future (“Taxes”).
                  </li>
                  <li>
                    You are responsible for all applicable Taxes that arise from or as a result of
                    your subscription to or purchase of SellOnLive’s products and services. To the
                    extent that SellOnLive charges these Taxes, they are calculated using the tax
                    rates that apply based on the billing address you provide to us. Such amounts
                    are in addition to the Fees for such products and services and will be billed to
                    your Authorized Payment Method. If you are exempt from payment of such Taxes,
                    you must provide us with evidence of your exemption, which in some jurisdictions
                    includes an original certificate that satisfies applicable legal requirements
                    attesting to tax-exempt status. Tax exemption will only apply from and after the
                    date we receive evidence satisfactory to SellOnLive of your exemption. If you
                    are not charged Taxes by SellOnLive, you are responsible for determining if
                    Taxes are payable, and if so, self-remitting Taxes to the appropriate tax
                    authorities in your jurisdiction.
                  </li>
                  <li>
                    For the avoidance of doubt, all sums payable by you to SellOnLive under these
                    Terms of Service shall be paid free and clear of any deductions or withholdings
                    whatsoever. Other than Taxes charged by SellOnLive to you and remitted to the
                    appropriate tax authorities on your behalf, any deductions or withholdings that
                    are required by law shall be borne by you and paid separately to the relevant
                    taxation authority. SellOnLive shall be entitled to charge the full amount of
                    Fees stipulated under these Terms of Service to your Authorized Payment Method
                    ignoring any such deduction or withholding that may be required.
                  </li>
                  <li>
                    You must maintain an accurate location in the administration menu of your
                    SellOnLive Store. If you change jurisdictions you must promptly update your
                    location in the administration menu.
                  </li>
                  <li>SellOnLive does not provide refunds.</li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  A valid payment method (like a credit card) is required for all stores. You will
                  be billed for your Subscription Fees every 30 days. Any Transaction Fees or
                  Additional Fees will be charged to your payment method. If we are not able to
                  process payment of Fees using your payment method, we will try again in 3 days. If
                  we are unable to process payment of Fees on the second attempt, we will make a
                  third and final attempt three days later. If payment of Fees is unsuccessful after
                  three attempts, SellOnLive may freeze your store. You may be required to remit
                  Taxes to SellOnLive or to self-remit to your local taxing authority. No refunds.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Cancellation">11. Cancellation and Termination</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    You may cancel your Account and terminate the Terms of Service at any time by
                    contacting{' '}
                    <a href="mailto:sellonlivetech@gmail.com" className="body-link">
                      sellonlivetech@gmail.com
                    </a>
                    and then following the specific instructions indicated to you in SellOnLive’s
                    response.
                  </li>
                  <li>
                    Upon termination of the Services by either party for any reason:
                    <ol>
                      <li>
                        SellOnLive will cease providing you with the Services and you will no longer
                        be able to access your Account;
                      </li>
                      <li>
                        unless otherwise provided in the Terms of Service, you will not be entitled
                        to any refunds of any Fees, pro rata or otherwise;
                      </li>
                      <li>
                        any outstanding balance owed to SellOnLive for your use of the Services
                        through the effective date of such termination will immediately become due
                        and payable in full; and
                      </li>
                      <li>your Store website will be taken offline.</li>
                    </ol>
                  </li>
                  <li>
                    If you purchased a domain name through SellOnLive, upon cancellation your domain
                    will no longer be automatically renewed. Following termination, it will be your
                    sole responsibility to handle all matters related to your domain with the domain
                    provider.
                  </li>
                  <li>
                    If at the date of termination of the Service, there are any outstanding Fees
                    owing by you, you will receive one final invoice via email. Once that invoice
                    has been paid in full, you will not be charged again.
                  </li>
                  <li>
                    We reserve the right to modify or terminate the SellOnLive Service, the Terms of
                    Service and/or your Account for any reason, without notice at any time (unless
                    otherwise required by applicable law). Termination of the Terms of Service shall
                    be without prejudice to any rights or obligations which arose prior to the date
                    of termination.
                  </li>
                  <li>
                    Fraud: Without limiting any other remedies, SellOnLive may suspend or terminate
                    your Account if we suspect that you (by conviction, settlement, insurance or
                    escrow investigation, or otherwise) have engaged in fraudulent activity in
                    connection with the use of the Services.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  To initiate a termination contact{' '}
                  <a href="mailto:sellonlivetech@gmail.com" className="body-link">
                    sellonlivetech@gmail.com
                  </a>
                  . SellOnLive will respond with specific information regarding the termination
                  process for your account. Once termination is confirmed, domains purchased through
                  SellOnLive will no longer be automatically renewed. If you cancel in the middle of
                  your billing cycle, you’ll have one last email invoice.
                </p>

                <p>
                  We may change or terminate your account at any time. Any fraud and we will suspend
                  or terminate your account.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="Modifications">12. Modifications to the Service and Prices</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <ol>
                  <li>
                    Prices for using the Services are subject to change upon 30 days’ notice from
                    SellOnLive. Such notice may be provided at any time by posting the changes to
                    the SellOnLive Site (SellOnLive.com) or the administration menu of your
                    SellOnLive Store via an announcement.
                  </li>
                  <li>
                    SellOnLive reserves the right at any time, and from time to time, to modify or
                    discontinue, the Services (or any part thereof) with or without notice (unless
                    otherwise required by applicable law).
                  </li>
                  <li>
                    SellOnLive shall not be liable to you or to any third party for any
                    modification, price change, suspension or discontinuance of the Service.
                  </li>
                </ol>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>We may change or discontinue the service at anytime, without liability.</p>
              </div>
            </aside>
          </div>

          <h2 id="BetaServices">13. Beta Services</h2>

          <div className="terms__section grid-container">
            <div className="">
              <div className="long-form-content ">
                <p>
                  From time to time, SellOnLive may, in its sole discretion, invite you to use, on a
                  trial basis, pre-release or beta features that are in development and not yet
                  available to all merchants (“Beta Services”). Beta Services may be subject to
                  additional terms and conditions, which SellOnLive will provide to you prior to
                  your use of the Beta Services. Such Beta Services and all associated conversations
                  and materials relating thereto will be considered SellOnLive Confidential
                  Information and subject to the confidentiality provisions in this agreement.
                  Without limiting the generality of the foregoing, you agree that you will not make
                  any public statements or otherwise disclose your participation in the Beta
                  Services without SellOnLive’s prior written consent. SellOnLive makes no
                  representations or warranties that the Beta Services will function. SellOnLive may
                  discontinue the Beta Services at any time in its sole discretion. SellOnLive will
                  have no liability for any harm or damage arising out of or in connection with a
                  Beta Service. The Beta Services may not work in the same way as a final version.
                  SellOnLive may change or not release a final or commercial version of a Beta
                  Service in our sole discretion.
                </p>
              </div>
            </div>
          </div>

          <h2 id="FeedbackAndReviews">14. Feedback and Reviews</h2>

          <div className="terms__section grid-container">
            <div className="">
              <div className="long-form-content ">
                <p>
                  SellOnLive welcomes any ideas and/or suggestions regarding improvements or
                  additions to the Services. Under no circumstances shall any disclosure of any
                  idea, suggestion or related material or any review of the Services, Third Party
                  Services or any Third Party Provider (collectively, “Feedback") to SellOnLive be
                  subject to any obligation of confidentiality or expectation of compensation. By
                  submitting Feedback to SellOnLive (whether submitted directly to SellOnLive or
                  posted on any SellOnLive hosted forum or page), you waive any and all rights in
                  the Feedback and that SellOnLive is free to implement and use the Feedback if
                  desired, as provided by you or as modified by SellOnLive, without obtaining
                  permission or license from you or from any third party. Any reviews of a Third
                  Party Service or Third Party Provider that you submit to SellOnLive must be
                  accurate to the best of your knowledge, and must not be illegal, obscene,
                  threatening, defamatory, invasive of privacy, infringing of intellectual property
                  rights, or otherwise injurious to third parties or objectionable. SellOnLive
                  reserves the right (but not the obligation) to remove or edit Feedback of Third
                  Party Services or Third Party Providers, but does not regularly inspect posted
                  Feedback.
                </p>
              </div>
            </div>
          </div>

          <h2 id="Dmca">15. DMCA Notice and Takedown Procedure</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  SellOnLive supports the protection of intellectual property and asks SellOnLive
                  merchants to do the same. It’s our policy to respond to all notices of alleged
                  copyright infringement. If someone believes that one of our merchants is
                  infringing their intellectual property rights, they can send a DMCA Notice to
                  SellOnLive’s designated agent using our form. Upon receiving a DMCA Notice, we may
                  remove or disable access to the Materials claimed to be a copyright infringement.
                  Once provided with a notice of takedown, the merchant can reply with a counter
                  notification using our form if they object to the complaint. The original
                  complainant has 14 business days after we receive a counter notification to seek a
                  court order restraining the merchant from engaging in the infringing activity,
                  otherwise we restore the material
                </p>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  SellOnLive respects intellectual property rights and you should too. If we receive
                  a DMCA Notice, we may disable access or remove the allegedly infringing content
                  from your website. If you don’t think the claim is valid, you can proceed with a
                  counter notification.
                </p>

                <p>
                  If you believe one of our merchants is infringing your intellectual property
                  rights, you can send SellOnLive a DMCA Notice. We will expeditiously disable
                  access or remove the content and notify the merchant.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="RightsOfThirdParties">16. Rights of Third Parties</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  Save for SellOnLive and its affiliates, SellOnLive Users or anyone accessing
                  SellOnLive Services pursuant to these Terms of Service, unless otherwise provided
                  in these Terms of Service, no person or entity who is not a party to these Terms
                  of Service shall have any right to enforce any term of these Terms of Service,
                  regardless of whether such person or entity has been identified by name, as a
                  member of a class or as answering a particular description. For the avoidance of
                  doubt, this shall not affect the rights of any permitted assignee or transferee of
                  these Terms.
                </p>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  Only SellOnLive, SellOnLive Users and persons accessing SellOnLive Services have
                  any rights under these Terms of Service.
                </p>
              </div>
            </aside>
          </div>

          <h2 id="DataProtection">17. Privacy &amp; Data Protection</h2>

          <div className="terms__section grid-container">
            <div className="grid-item grid-5">
              <div className="long-form-content ">
                <p>
                  SellOnLive is firmly committed to protecting the privacy of your personal
                  information and the personal information of your customers. By using the Service,
                  you acknowledge and agree that SellOnLive’s collection, usage and disclosure of
                  this personal information is governed by our{' '}
                  <a href="https://www.sellonlive.tech/legal/privacy" className="body-link">
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>

            <aside className="grid-item grid-3 grid--last terms__aside">
              <h3 className="heading--5">Which means</h3>
              <div className="long-form-content ">
                <p>
                  SellOnLive’s use and collection of personal information is governed by our Privacy
                  Policy.
                  <br />
                  <br />
                  16-2D, Jalan BS 1/3, Taman Bukit Serdang, Pusat Perdagangan Olive Hill, Sekysen 1,
                  Seri Kembangan, 10, 43300, Malaysia
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const getServerSideProps = async ({ locale }) => {
  return {
    props: {
      locale,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default withStyles(styles)(Index);
